import React, { useState, useCallback, useRef } from 'react';
import { Send, Loader2, Paperclip, Sparkles, Folder, Edit3 } from 'lucide-react';

interface ChatInputProps {
  onSendMessage: (message: string) => Promise<void>;
  isLoading: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading }) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage || isLoading || isSending) return;

    try {
      setIsSending(true);
      setMessage(''); // Clear input immediately for better UX
      await onSendMessage(trimmedMessage);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore message on error
      setMessage(trimmedMessage);
      // Focus input for retry
      inputRef.current?.focus();
    } finally {
      setIsSending(false);
    }
  }, [message, isLoading, isSending, onSendMessage]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  }, [handleSubmit]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  }, []);

  const isDisabled = isLoading || isSending;
  const showSpinner = isLoading || isSending;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative p-[1px] rounded-2xl bg-gray-300 focus-within:bg-gradient-to-br focus-within:from-pink-500 focus-within:via-orange-400/40 focus-within:to-transparent transition-all duration-300 focus-within:shadow-lg focus-within:shadow-pink-500/20">
        <div className="relative bg-gray-50 rounded-2xl min-h-[120px] p-4 focus-within:bg-white transition-all duration-300">
        <textarea
          ref={inputRef}
          value={message}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={isDisabled ? "Please wait..." : "How can I help you with your wedding today?"}
          className="w-full h-16 bg-transparent text-base placeholder-gray-500 resize-none focus:outline-none disabled:opacity-50 pb-12"
          disabled={isDisabled}
          autoComplete="off"
          maxLength={500}
          rows={2}
        />
        
        {/* Bottom section with action buttons and send */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
          {/* Action buttons */}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-pink-500 hover:bg-white rounded-lg transition-all duration-200"
              title="Attach file"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-pink-500 hover:bg-white rounded-lg transition-all duration-200"
              title="AI suggestions"
            >
              <Sparkles className="w-4 h-4" />
            </button>
            
          </div>
          
          {/* Send button */}
          <button
            type="submit"
            disabled={isDisabled || !message.trim()}
            className="p-2.5 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl hover:from-pink-600 hover:to-orange-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg disabled:from-gray-300 disabled:to-gray-400"
            title={isDisabled ? "Please wait..." : "Send message (Enter)"}
          >
            {showSpinner ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        </div>
      </div>
    </form>
  );
};